import {Component, HostListener, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Modifier} from '../../../_models/modifier';
import {ModifierGroup} from '../../../_models/modifierGroup';
import {Item} from '../../../_models/Item';
import {CloverApiService} from '../../../_services/cloverApi.service';
import {AlertService} from '../../../_services';
import {User} from '../../../_models';
import {AESEncryptDecryptService} from '../../../_services/aesencrypt-decrypt.service';
import {Constants} from 'src/app/constants/constants';
import {UtilsService} from '../../../_services/utils.service';

@Component({
  selector: 'app-item-options-dialog-component',
  templateUrl: './item-options-dialog-component.component.html',
  styleUrls: ['./item-options-dialog-component.component.scss'],
})
export class ItemOptionsDialogComponentComponent implements OnInit {
  Constants = Constants;
  description: string;
  item: Item;
  modifierGroups: ModifierGroup[] = [];
  modifiers: Modifier[] = [];
  selectedOptions: Modifier[] = [];
  currentUser: User;
  selectedRole: String;
  total = 0;
  selectionError = false;
  modificationCount = 0;
  modifiers_loading = true;
  firstTime = true;
  smallWidth = false;
  smallHeight = false;

  title = '';
  quantity = 1;
  loading = true;
  checkoutMode = false;

  // Custom modifier
  showCustomModInput = false;
  customModName = '';
  customModPrice: number = null;

  // Note
  customNote = '';

  // Edit mode (#1501): pre-populate when editing an existing cart item
  editMode = false;
  private existingModifierIds: string[] = [];

  constructor(private dialogRef: MatDialogRef<ItemOptionsDialogComponentComponent>, @Inject(MAT_DIALOG_DATA) data,
              private alertService: AlertService, private cloverApiService: CloverApiService, private utilsService: UtilsService,
              private _AESEncryptDecryptService: AESEncryptDecryptService) {
    this.description = data.title;
    this.item = data.options;
    this.checkoutMode = data.checkoutMode;

    if (data.editMode) {
      this.editMode = true;
      this.customNote = data.existingNote || '';
      this.quantity = data.existingQty || 1;
      // Capture existing modifier IDs so we can pre-check after groups load
      const existing: any[] = data.existingModifiers || [];
      this.existingModifierIds = existing.map(m => m?.modifier?.id || m?.id).filter(Boolean);
    }

    this.currentUser = utilsService.getCurrentUser();
    this.selectedRole = utilsService.getSelectedRoleFromSession();

    this.smallWidth = window.innerWidth <= 600;
    this.smallHeight = window.innerHeight <= 850;
  }

  @HostListener('window:resize', ['$event'])
  onResize($event) {
    this.smallWidth = $event.target.innerWidth <= 600;
    this.smallHeight = window.innerHeight <= 850;
  }

  get canAddCustomModifier(): boolean {
    const role = this.selectedRole;
    return role === Constants.ADMIN || role === Constants.MANAGER || role === Constants.SERVER;
  }

  plus() { this.quantity++; }

  minus() {
    if (this.quantity > 1) { this.quantity--; }
  }

  ngOnInit() {
    if (this.item.id) {
      this.loadAllModifierGroupsByItemId(this.item.id);
    } else {
      this.loadAllModifierGroupsByItemId(this.item._id);
    }
  }

  private loadAllModifierGroupsByItemId(itemId: string) {
    this.cloverApiService.getModifierGroupsByItemId(itemId).subscribe(
      (modifierG: ModifierGroup[]) => {
        Object.assign(this.modifierGroups, modifierG);
        this.initModifierSelected();
        if (this.editMode && this.existingModifierIds.length > 0) {
          this.preCheckExistingModifiers();
        }
        this.loading = false;
        this.modifiers_loading = false;
      },
      error => {
        this.alertService.error(error);
        this.loading = false;
        this.modifiers_loading = false;
      });
  }

  initModifierSelected() {
    if (this.modifierGroups?.length > 0) {
      this.modifierGroups.forEach(group => {
        group.selected = 0;
        if (group.modifiers) {
          group.modifiers.forEach(mod => { mod.checked = false; });
        }
      });
    }
    this.firstTime = false;
  }

  private preCheckExistingModifiers(): void {
    this.modifierGroups.forEach((group, groupIndex) => {
      if (!group.modifiers) { return; }
      group.modifiers.forEach(mod => {
        if (this.existingModifierIds.includes(mod.id)) {
          this.toggleModifier(groupIndex, mod, group.maxAllowed);
        }
      });
    });
  }

  toggleModifier(groupIndex: number, modifier: Modifier, maxAllowed: number): void {
    if (this.firstTime) { this.initModifierSelected(); }

    const group = this.modifierGroups[groupIndex];
    if (!group) { return; }

    // Always read the authoritative max from the group itself — the
    // click-bound `maxAllowed` argument can drift when the dialog gets
    // re-used and the template re-renders with stale values. Falling back
    // to the group lets a single source of truth drive enforcement.
    const groupMax = (group.maxAllowed != null && group.maxAllowed > 0)
      ? group.maxAllowed
      : ((maxAllowed != null && maxAllowed > 0) ? maxAllowed : 0);

    if (modifier.checked) {
      modifier.checked = false;
      group.selected = Math.max(0, (group.selected || 0) - 1);
      const pos = this.selectedOptions.indexOf(modifier);
      if (pos !== -1) { this.selectedOptions.splice(pos, 1); }
    } else {
      if (groupMax > 0 && (group.selected || 0) >= groupMax) {
        return;
      }
      modifier.checked = true;
      group.selected = (group.selected || 0) + 1;
      this.selectedOptions.push(modifier);
    }
  }

  isModifierDisabled(group: ModifierGroup, modifier: Modifier): boolean {
    if (modifier.checked) { return false; }
    const max = group?.maxAllowed;
    return max != null && max > 0 && (group.selected || 0) >= max;
  }

  // Legacy method for backwards compat
  onItemSelect(index: number, modifier: Modifier, event, max) {
    this.toggleModifier(index, modifier, max);
  }

  addCustomModifier(): void {
    if (!this.customModName?.trim()) { return; }
    const mod = new Modifier();
    mod.id = 'custom-' + Date.now();
    mod.name = this.customModName.trim();
    mod.price = Math.round((this.customModPrice || 0) * 100);
    mod.checked = true;
    this.selectedOptions.push(mod);
    this.customModName = '';
    this.customModPrice = null;
    this.showCustomModInput = false;
  }

  getRunningTotal(): string {
    let total = this.item.price * this.quantity;
    this.selectedOptions.forEach(opt => {
      total += (opt.price || 0) * this.quantity;
    });
    return (total / 100).toFixed(2);
  }

  private checkSelectionError(): boolean {
    this.selectionError = false;
    this.modifierGroups.forEach(group => {
      const selected = group.selected || 0;
      const min = group.minRequired != null ? group.minRequired : 0;
      const max = group.maxAllowed != null && group.maxAllowed > 0 ? group.maxAllowed : Infinity;
      if (selected < min || selected > max) {
        this.selectionError = true;
      }
    });
    return this.selectionError;
  }

  addToCart() {
    if (this.firstTime) { this.initModifierSelected(); }
    if (this.checkSelectionError()) { return; }

    this.dialogRef.close({
      selectedOptions: this.selectedOptions,
      note: this.customNote,
      qty: this.quantity,
      checkout: false
    });
  }

  addAndRouteToCart() {
    if (this.firstTime) { this.initModifierSelected(); }
    if (this.checkSelectionError()) { return; }

    this.dialogRef.close({
      selectedOptions: this.selectedOptions,
      note: this.customNote,
      qty: this.quantity,
      checkout: true
    });
  }

  close() {
    this.dialogRef.close();
  }
}
